import type { TileViewer } from "@virdx/wsi-viewer"
import { type RefObject, useEffect, useState } from "react"

interface UseOverlayPeekParams {
  viewerRef: RefObject<TileViewer | null>
  viewerEpoch: number
  /**
   * Binds Space only while true (the pathology label-raster workspace). Turning
   * it off detaches the listeners and releases an active peek.
   */
  enabled: boolean
}

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "url",
  "tel",
  "email",
  "password",
  "number",
  "date",
  "datetime-local",
  "month",
  "week",
  "time"
])

/** True when a key press on `target` types into a text field, so shortcuts must stay out of the way. */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === "TEXTAREA" || tag === "SELECT") return true
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type.toLowerCase()
    return TEXT_INPUT_TYPES.has(type)
  }
  return false
}

function isSpaceKey(event: KeyboardEvent): boolean {
  return event.code === "Space" || event.key === " "
}

/**
 * Hold Space to peek at the tissue under the overlays: while the key is down
 * every overlay is hidden at render time (`TileViewer.setOverlaysPeekHidden`)
 * and restored on release. Nothing else changes, so the active brush / eraser /
 * pick tool, its radius and the opacity slider all keep their state.
 *
 * Listens on `window` so the peek works whichever panel has focus, ignores key
 * repeat, modifier combinations and text inputs, and releases on window blur /
 * tab hide because the matching `keyup` may never arrive. The viewer itself has
 * no Space binding, so a click + drag while peeking still uses the active tool.
 */
export function useOverlayPeek({ viewerRef, viewerEpoch, enabled }: UseOverlayPeekParams) {
  const [isPeeking, setIsPeeking] = useState(false)

  useEffect(() => {
    if (!enabled) return
    let held = false
    const apply = (hidden: boolean) => {
      if (held === hidden) return
      held = hidden
      viewerRef.current?.setOverlaysPeekHidden(hidden)
      setIsPeeking(hidden)
    }
    const release = () => apply(false)

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSpaceKey(event) || event.metaKey || event.ctrlKey || event.altKey) return
      if (isTextEntryTarget(event.target)) return
      // Space would otherwise scroll the page or activate a focused button.
      event.preventDefault()
      if (event.repeat) return
      apply(true)
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (!(isSpaceKey(event) && held)) return
      event.preventDefault()
      release()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") release()
    }

    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("keyup", onKeyUp)
    window.addEventListener("blur", release)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("blur", release)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      release()
    }
  }, [enabled, viewerEpoch, viewerRef])

  return { isPeeking }
}
